import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { aiAPI } from '../../data/ai';
import { unwrap } from '../../data/api';
import { resolvePageId } from '../../utils/pageAiContext';

export const aiKeys = {
  context: (pageId) => ['ai-context', pageId],
  sessions: ['ai-sessions'],
  session: (sessionId) => ['ai-session', sessionId],
};


export function useCurrentPageId() {
  const { pathname } = useLocation();
  return resolvePageId(pathname);
}

export function usePageAiContextQuery(pageId, options = {}) {
  return useQuery({
    queryKey: aiKeys.context(pageId),
    queryFn: () => aiAPI.getContext(pageId).then((r) => unwrap(r)),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

export function useAiSessionsQuery(options = {}) {
  return useQuery({
    queryKey: aiKeys.sessions,
    queryFn: () => aiAPI.listSessions().then((r) => unwrap(r, [])),
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useAiSessionQuery(sessionId, options = {}) {
  return useQuery({
    queryKey: aiKeys.session(sessionId),
    queryFn: () => aiAPI.getSession(sessionId).then((r) => unwrap(r)),
    enabled: Boolean(sessionId),
    ...options,
  });
}

export function useEvolutionAiChatMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pageId, message, history, sessionId }) =>
      aiAPI.chat({ pageId, message, history, sessionId }).then((r) => {
        if (r.success) return r.data;
        throw r.error;
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: aiKeys.sessions });
      if (data?.session_id) {
        queryClient.invalidateQueries({ queryKey: aiKeys.session(data.session_id) });
      }
    },
  });
}
